import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ChainOfCommand({ user }) {
  const [chainData, setChainData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChainOfCommand();
  }, []);

  const fetchChainOfCommand = async () => {
    try {
      const response = await axios.get(`${API}/chain-of-command`);
      setChainData(response.data);
    } catch (error) {
      console.error('Failed to fetch chain of command:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (rankName) => {
    const colors = {
      Command: 'bg-yellow-600 border-yellow-500',
      'Commissioned Officer': 'bg-purple-600 border-purple-500',
      Warrant: 'bg-blue-600 border-blue-500',
      NCO: 'bg-green-600 border-green-500',
      Enlisted: 'bg-gray-600 border-gray-500',
    };
    return colors[rankName] || 'bg-gray-600 border-gray-500';
  };

  const getRankIcon = (rankName) => {
    const icons = {
      Command: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ),
      'Commissioned Officer': (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      ),
      default: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      ),
    };
    return icons[rankName] || icons.default;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white text-xl">Loading chain of command...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chain-of-command-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Chain of Command</h1>
        <p className="text-gray-400">Organizational hierarchy and succession planning</p>
      </div>

      {/* Chain of Command Visualization */}
      <div className="space-y-6">
        {Object.entries(chainData).map(([rankName, personnel], index) => (
          <div key={rankName} className="relative">
            {/* Rank Header */}
            <div className="flex items-center space-x-4 mb-4">
              <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${getRankColor(rankName)}`}>
                <div className="text-white">{getRankIcon(rankName)}</div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{rankName}</h2>
                <p className="text-gray-400 text-sm">{personnel.length} personnel</p>
              </div>
            </div>

            {/* Personnel Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ml-16">
              {personnel.map((person) => (
                <div
                  key={person.id}
                  className={`bg-gray-800 rounded-lg p-4 border-l-4 ${getRankColor(rankName)} hover:bg-gray-700 transition duration-200`}
                  data-testid={`chain-card-${person.username}`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold truncate">{person.full_name}</p>
                      <p className="text-gray-400 text-sm">@{person.username}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {person.roles?.slice(0, 3).map((role) => (
                          <span
                            key={role.id}
                            className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded"
                          >
                            {role.name}
                          </span>
                        ))}
                        {person.roles?.length > 3 && (
                          <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded">
                            +{person.roles.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Connecting Line */}
            {index < Object.entries(chainData).length - 1 && (
              <div className="flex justify-center my-4">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Succession Planning Note */}
      <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <svg className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="text-blue-300 font-bold mb-2">Succession Planning Active</h3>
            <p className="text-blue-200 text-sm">
              The Guardian Corps maintains a robust chain of command to ensure operational continuity. In the event of
              Commander absence (up to 90 days), authority is delegated according to rank hierarchy with limited
              operations protocols in effect.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChainOfCommand;
